import { useState, useEffect, useMemo } from "react";
import { X, CheckCircle, AlertCircle, Loader2, Clock, Calendar, Smartphone, CreditCard, Check, XCircle, RefreshCw, MessageCircle, Copy, Award, Briefcase, TrendingUp, ChevronDown, Sparkles } from "lucide-react";
import enrollmentPaymentAPI from '@/services/enrollmentApi';

interface EnrollmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface Week {
  weekNumber: number;
  topic: string;
  price: number;
  startDate: Date;
  endDate: Date;
  status: 'active' | 'upcoming' | 'completed';
  skill: string;
}

interface PricePreview {
  selectedWeeks: number[];
  weeksCount: number;
  pricePerWeek: number;
  totalAmount: number;
  discount: number;
  finalAmount: number;
  trainingMode: "VIRTUAL" | "HYBRID";
}

interface FormData {
  name: string;
  email: string;
  phoneNumber: string;
  tutorialType: "VIRTUAL" | "HYBRID";
}

type FlowStep = "form" | "payment-phone" | "processing" | "polling" | "success" | "failed" | "timeout";
type PaymentMethod = "stk" | "manual";

const TRAINING_WEEKS: Week[] = [
  { weekNumber: 1, topic: "Content Training Introduction", price: 3500, startDate: new Date('2026-02-17'), endDate: new Date('2026-02-21'), status: 'upcoming', skill: "Create viral content" },
  { weekNumber: 2, topic: "Practical Content Creation - Video Editing & Enhancement", price: 3500, startDate: new Date('2026-02-24'), endDate: new Date('2026-02-28'), status: 'upcoming', skill: "Professional editing" },
  { weekNumber: 3, topic: "Meta Ads - Facebook & Instagram Ads (Practical)", price: 3500, startDate: new Date('2026-03-03'), endDate: new Date('2026-03-07'), status: 'upcoming', skill: "Run profitable ads" },
  { weekNumber: 4, topic: "TikTok Ads + WhatsApp Sales Bonus", price: 3500, startDate: new Date('2026-03-10'), endDate: new Date('2026-03-12'), status: 'upcoming', skill: "Convert to sales" }
];

const PRICING = {
  VIRTUAL: { monthly: 13000, weekly: 3500 },
  HYBRID: { monthly: 25000, weekly: 6500 }
};

const POLLING_CONFIG = { INTERVAL_MS: 3000, TIMEOUT_MS: 60000 };

function getWeekStatus(week: Week): 'active' | 'upcoming' | 'completed' {
  const now = new Date();
  if (now < week.startDate) return 'upcoming';
  if (now > week.endDate) return 'completed';
  return 'active';
}

function ProgressIndicator({ currentStep }: { currentStep: FlowStep }) {
  const steps = ["Choose", "Details", "Secure", "Done"];
  const stepMap: Record<FlowStep, number> = {
    form: 0, "payment-phone": 1, processing: 2, polling: 2, success: 3, failed: 2, timeout: 2
  };
  const currentIndex = stepMap[currentStep];

  return (
    <div className="flex items-center justify-between mb-8">
      {steps.map((label, index) => (
        <div key={label} className="flex items-center flex-1">
          <div className="flex flex-col items-center flex-1">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
              index < currentIndex ? "bg-emerald-500 text-white" :
              index === currentIndex ? "bg-maroon text-white scale-110" :
              "bg-gray-200 text-gray-400"
            }`}>
              {index < currentIndex ? <Check className="w-5 h-5" /> : index + 1}
            </div>
            <span className={`text-xs mt-2 font-medium ${index === currentIndex ? "text-gray-900" : "text-gray-500"}`}>
              {label}
            </span>
          </div>
          {index < steps.length - 1 && (
            <div className={`h-1 flex-1 mx-2 rounded transition-all ${index < currentIndex ? "bg-emerald-500" : "bg-gray-200"}`} />
          )}
        </div>
      ))}
    </div>
  );
}

function PaybillDetails({ onCopy }: { onCopy: (text: string, label: string) => void }) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <CreditCard className="w-5 h-5 text-maroon" />
        M-Pesa Paybill Payment
      </h3>
      <div className="space-y-3">
        {[
          { label: "Paybill", value: "542542" },
          { label: "Account", value: "544323" },
          { label: "Business", value: "Ruana Solutions", noCopy: true }
        ].map(({ label, value, noCopy }) => (
          <div key={label} className="flex justify-between items-center bg-white rounded p-3 border border-gray-200">
            <div>
              <p className="text-xs text-gray-600">{label}</p>
              <p className="text-lg font-mono font-bold text-gray-900">{value}</p>
            </div>
            {!noCopy && (
              <button onClick={() => onCopy(value, label)} className="p-2 hover:bg-gray-100 rounded transition">
                <Copy className="w-4 h-4 text-gray-600" />
              </button>
            )}
          </div>
        ))}
      </div>
      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded">
        <p className="text-sm text-amber-900">
          Send M-Pesa SMS to <strong>+254 704 544 323</strong> after payment
        </p>
      </div>
    </div>
  );
}


function FormField({ 
  label, value, onChange, onBlur, error, type = "text", placeholder, required = false 
}: {
  label: string; value: string; onChange: (v: string) => void; onBlur?: () => void;
  error?: string; type?: string; placeholder?: string; required?: boolean;
}) {
  const hasValue = value.trim().length > 0;
  const isValid = hasValue && !error;
  
  return (
    <div>
      <label className="block text-sm font-medium mb-2 text-gray-900">
        {label} {required && <span className="text-maroon">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-maroon transition-all text-gray-900 placeholder-gray-400 ${
          error ? 'border-red-500 bg-red-50' : 
          isValid ? 'border-emerald-500 bg-white' :
          'border-gray-300 bg-white'
        }`}
        placeholder={placeholder}
        required={required}
      />
      {error && (
        <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
          <AlertCircle className="w-4 h-4" />
          {error}
        </p>
      )}
      {isValid && !error && (
        <p className="text-sm text-emerald-600 mt-1 flex items-center gap-1">
          <CheckCircle className="w-4 h-4" />
          Looks good
        </p>
      )}
    </div>
  );
}

export default function EnrollmentModal({ open, onOpenChange }: EnrollmentModalProps) {
  const [step, setStep] = useState<FlowStep>("form");
  const [loading, setLoading] = useState(false);
  const [weeks, setWeeks] = useState<Week[]>(TRAINING_WEEKS);
  const [selectedWeeks, setSelectedWeeks] = useState<Set<number>>(new Set([1, 2, 3, 4])); // Pre-select all 4 weeks
  const [showWeeklyOptions, setShowWeeklyOptions] = useState(false); // Control weekly view
  const [enrollmentId, setEnrollmentId] = useState<number | null>(null);
  const [transactionRef, setTransactionRef] = useState<string | null>(null);
  const [mpesaPhone, setMpesaPhone] = useState("");
  const [mpesaReceiptNumber, setMpesaReceiptNumber] = useState<string | null>(null);
  const [failureReason, setFailureReason] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("stk");
  const [pollingElapsed, setPollingElapsed] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<FormData>({
    name: "", email: "", phoneNumber: "", tutorialType: "VIRTUAL"
  });

  useEffect(() => {
    if (open) {
      setWeeks(TRAINING_WEEKS.map(week => ({ ...week, status: getWeekStatus(week) })));
    }
  }, [open]);

  const pricePreview = useMemo((): PricePreview | null => {
    if (selectedWeeks.size === 0) return null;
    
    const selectedArray = Array.from(selectedWeeks);
    const weeksCount = selectedArray.length;
    const mode = formData.tutorialType;
    
    let pricePerWeek: number, baseTotal: number, finalAmount: number;
    
    if (weeksCount === 4) {
      finalAmount = PRICING[mode].monthly;
      pricePerWeek = mode === "VIRTUAL" ? 3500 : 6500;
      baseTotal = weeksCount * pricePerWeek;
    } else {
      pricePerWeek = PRICING[mode].weekly;
      baseTotal = weeksCount * pricePerWeek;
      finalAmount = baseTotal;
    }
    
    return {
      selectedWeeks: selectedArray,
      weeksCount,
      pricePerWeek,
      totalAmount: baseTotal,
      discount: baseTotal - finalAmount,
      finalAmount,
      trainingMode: mode
    };
  }, [selectedWeeks, formData.tutorialType]);

  useEffect(() => {
    if (step !== "polling") {
      setPollingElapsed(0);
      return;
    }
    const interval = setInterval(() => setPollingElapsed(prev => prev + 1), 1000);
    return () => clearInterval(interval);
  }, [step]);

  useEffect(() => {
    if (step !== "polling" || !transactionRef) return;

    let isCancelled = false;
    
    const pollInterval = setInterval(async () => {
      if (isCancelled) return;
      try {
        const status = await enrollmentPaymentAPI.checkPaymentStatus(transactionRef);
        if (isCancelled) return;
        
        if (status.paymentStatus === "SUCCESS") {
          setMpesaReceiptNumber(status.mpesaReceiptNumber || null);
          setStep("success");
        } else if (status.paymentStatus === "FAILED") {
          setFailureReason(status.failureReason || "Payment failed");
          setStep("failed");
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    }, POLLING_CONFIG.INTERVAL_MS);

    const timeout = setTimeout(() => {
      if (!isCancelled) setStep("timeout");
    }, POLLING_CONFIG.TIMEOUT_MS);

    return () => {
      isCancelled = true;
      clearInterval(pollInterval);
      clearTimeout(timeout);
    };
  }, [step, transactionRef]);

  const toggleWeek = (weekNum: number) => {
    const week = weeks.find(w => w.weekNumber === weekNum);
    if (!week || week.status === 'completed') return;

    setSelectedWeeks(prev => {
      const next = new Set(prev);
      next.has(weekNum) ? next.delete(weekNum) : next.add(weekNum);
      return next;
    });
    setErrors(prev => {
      const next = { ...prev };
      delete next.weeks;
      return next;
    });
  };

  const selectMonthlyBundle = (mode: "VIRTUAL" | "HYBRID") => {
    setFormData({ ...formData, tutorialType: mode });
    setSelectedWeeks(new Set([1, 2, 3, 4]));
    setErrors(prev => {
      const next = { ...prev };
      delete next.weeks;
      return next;
    });
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim() || formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }
    if (!formData.email.includes('@')) {
      newErrors.email = "Enter a valid email address";
    }
    const phoneRegex = /^(0|254|\+254)[17]\d{8}$/;
    if (!phoneRegex.test(formData.phoneNumber.replace(/[\s\-]/g, ''))) {
      newErrors.phoneNumber = "Enter a valid Kenyan phone number (07XX or 01XX)";
    }
    if (selectedWeeks.size === 0) {
      newErrors.weeks = "Please select at least one week";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEnrollmentSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setStep("processing");
    
    try {
      const response = await enrollmentPaymentAPI.createEnrollment({
        ...formData,
        selectedWeeks: Array.from(selectedWeeks)
      });
      
      setEnrollmentId(response.id);
      setMpesaPhone(response.phoneNumber);
      setStep("payment-phone");
    } catch (error: unknown) {
      setErrors({ submit: error instanceof Error ? error.message : "Enrollment failed" });
      setStep("form");
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSubmit = async () => {
    if (paymentMethod === "stk" && !mpesaPhone) {
      setErrors({ mpesaPhone: "Enter your M-Pesa phone number" });
      return;
    }

    setLoading(true);
    setStep("processing");
    
    try {
      if (paymentMethod === "manual") {
        await new Promise(resolve => setTimeout(resolve, 1000));
        setStep("success");
      } else {
        if (!enrollmentId) throw new Error("Enrollment ID missing");
        const response = await enrollmentPaymentAPI.initiatePayment(enrollmentId, mpesaPhone);
        setTransactionRef(response.transactionReference);
        setStep("polling");
      }
    } catch (error: unknown) {
      setFailureReason(error instanceof Error ? error.message : "Payment failed");
      setStep("failed");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    alert(`${label} copied!`);
  };

  const resetAndClose = () => {
    setFormData({ name: "", email: "", phoneNumber: "", tutorialType: "VIRTUAL" });
    setSelectedWeeks(new Set([1, 2, 3, 4])); // Reset to monthly bundle
    setShowWeeklyOptions(false); // Reset view
    setEnrollmentId(null);
    setTransactionRef(null);
    setMpesaPhone("");
    setMpesaReceiptNumber(null);
    setFailureReason(null);
    setPaymentMethod("stk");
    setErrors({});
    setStep("form");
    onOpenChange(false);
  };

  if (!open) return null;

  const getStepTitle = () => {
    switch (step) {
      case "form": return "Start Your Digital Marketing Career";
      case "payment-phone": return "Secure Your Spot";
      case "processing": return "Processing...";
      case "polling": return "Confirming...";
      case "success": return paymentMethod === "manual" ? "Spot Reserved!" : "Welcome Aboard! 🎉";
      case "failed": return "Almost There";
      case "timeout": return "Checking Status";
      default: return "Join Now";
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-maroon text-white p-6 rounded-t-2xl z-10">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-bold">{getStepTitle()}</h2>
            <button 
              onClick={resetAndClose} 
              className="p-2 hover:bg-white/20 rounded-lg transition"
              disabled={step === "processing" || step === "polling"}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {step === "form" && (
            <p className="text-sm text-white/90">Complete 4-week program • Feb 17 - Mar 12, 2026</p>
          )}
        </div>

        <div className="p-6">
          {step !== "form" && <ProgressIndicator currentStep={step} />}

          {step === "form" && (
            <div className="space-y-8">
              {/* Benefits Banner */}
              <div className="bg-gradient-to-r from-emerald-50 to-turquoise-50 border border-emerald-200 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <Award className="w-6 h-6 text-emerald-600 mx-auto mb-1" />
                    <p className="text-xs font-semibold text-gray-900">Certified</p>
                  </div>
                  <div>
                    <Briefcase className="w-6 h-6 text-emerald-600 mx-auto mb-1" />
                    <p className="text-xs font-semibold text-gray-900">Job Ready</p>
                  </div>
                  <div>
                    <TrendingUp className="w-6 h-6 text-emerald-600 mx-auto mb-1" />
                    <p className="text-xs font-semibold text-gray-900">Portfolio</p>
                  </div>
                </div>
              </div>

              {/* Monthly Bundle Selection OR Weekly Options */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Choose Your Program</h3>
                
                {!showWeeklyOptions ? (
                  // MONTHLY BUNDLE VIEW (DEFAULT)
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600 mb-4">Complete 4-week program with all essential skills</p>
                    
                    {/* Virtual Monthly Bundle */}
                    <button
                      type="button"
                      onClick={() => selectMonthlyBundle("VIRTUAL")}
                      className={`w-full text-left p-5 rounded-lg border-2 transition-all ${
                        selectedWeeks.size === 4 && formData.tutorialType === "VIRTUAL"
                          ? 'border-maroon bg-red-50 shadow-md'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-bold text-gray-900">Complete Online Program</h4>
                            <span className="text-xs bg-emerald-500 text-white px-2 py-1 rounded-full font-bold">
                              MOST POPULAR
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">4 weeks • 100% Virtual • Evening classes</p>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-500 line-through">KSh 14,000</div>
                          <div className="text-2xl font-bold text-maroon">KSh 13,000</div>
                          <div className="text-xs text-emerald-600 font-semibold">Save KSh 1,000</div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 bg-white/50 rounded-lg p-3">
                        {weeks.map(week => (
                          <div key={week.weekNumber} className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                            <div className="text-xs text-gray-700">
                              <span className="font-semibold">Week {week.weekNumber}:</span> {week.skill}
                            </div>
                          </div>
                        ))}
                      </div>
                    </button>

                    {/* Hybrid Monthly Bundle */}
                    <button
                      type="button"
                      onClick={() => selectMonthlyBundle("HYBRID")}
                      className={`w-full text-left p-5 rounded-lg border-2 transition-all ${
                        selectedWeeks.size === 4 && formData.tutorialType === "HYBRID"
                          ? 'border-maroon bg-red-50 shadow-md'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-bold text-gray-900">Complete Hybrid Program</h4>
                            <span className="text-xs bg-turquoise text-white px-2 py-1 rounded-full font-bold">
                              PREMIUM
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">4 weeks • Physical + Online • Better networking</p>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-500 line-through">KSh 26,000</div>
                          <div className="text-2xl font-bold text-maroon">KSh 25,000</div>
                          <div className="text-xs text-emerald-600 font-semibold">Save KSh 1,000</div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 bg-white/50 rounded-lg p-3">
                        {weeks.map(week => (
                          <div key={week.weekNumber} className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                            <div className="text-xs text-gray-700">
                              <span className="font-semibold">Week {week.weekNumber}:</span> {week.skill}
                            </div>
                          </div>
                        ))}
                      </div>
                    </button>

                    {/* Expand to Weekly Options */}
                    <button
                      type="button"
                      onClick={() => setShowWeeklyOptions(true)}
                      className="w-full flex items-center justify-center gap-2 py-3 px-4 text-sm font-medium text-gray-700 hover:text-maroon hover:bg-gray-50 rounded-lg border border-gray-300 hover:border-maroon/50 transition-all"
                    >
                      <ChevronDown className="w-4 h-4" />
                      Select specific weeks instead
                    </button>
                  </div>
                ) : (
                  // WEEKLY OPTIONS VIEW
                  <div className="space-y-3">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm text-gray-600">Select individual weeks that match your goals</p>
                      <button
                        type="button"
                        onClick={() => {
                          setShowWeeklyOptions(false);
                          selectMonthlyBundle("VIRTUAL");
                        }}
                        className="text-xs text-maroon hover:underline font-medium"
                      >
                        ← Back to Monthly
                      </button>
                    </div>

                    {/* Training Mode Selection */}
                    <div className="bg-gray-50 rounded-lg p-3 mb-4">
                      <label className="text-xs font-medium text-gray-700 mb-2 block">Training Mode</label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { value: "VIRTUAL" as const, label: "Online", price: "KSh 3,500/week" },
                          { value: "HYBRID" as const, label: "Hybrid", price: "KSh 6,500/week" }
                        ].map(({ value, label, price }) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setFormData({...formData, tutorialType: value})}
                            className={`p-2 rounded border text-sm transition ${
                              formData.tutorialType === value
                                ? "border-maroon bg-red-50 font-semibold"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            <div className="font-medium text-gray-900">{label}</div>
                            <div className="text-xs text-gray-600">{price}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Individual Week Selection */}
                    {weeks.map((week) => {
                      const isCompleted = week.status === 'completed';
                      const isActive = week.status === 'active';
                      const isSelected = selectedWeeks.has(week.weekNumber);
                      
                      return (
                        <button
                          key={week.weekNumber}
                          type="button"
                          onClick={() => toggleWeek(week.weekNumber)}
                          disabled={isCompleted}
                          className={`w-full text-left p-4 rounded-lg border transition-all ${
                            isCompleted
                              ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                              : isSelected
                              ? 'border-maroon border-l-4 bg-red-50'
                              : isActive
                              ? 'border-emerald-300 bg-emerald-50'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold text-gray-900">Week {week.weekNumber}</span>
                                {isActive && (
                                  <span className="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded-full font-bold">
                                    LIVE
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mb-1">{week.topic}</p>
                              <p className="text-xs font-semibold text-emerald-600">✓ {week.skill}</p>
                            </div>
                            <div className="text-sm font-bold text-gray-900">
                              KSh {formData.tutorialType === "VIRTUAL" ? "3,500" : "6,500"}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                    
                    {errors.weeks && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.weeks}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Personal Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Your Information</h3>
                
                <FormField
                  label="Full Name"
                  value={formData.name}
                  onChange={(v) => setFormData({...formData, name: v})}
                  onBlur={() => {
                    if (formData.name.trim() && formData.name.trim().length < 2) {
                      setErrors({...errors, name: "Name must be at least 2 characters"});
                    } else {
                      const { name, ...rest } = errors;
                      setErrors(rest);
                    }
                  }}
                  error={errors.name}
                  placeholder="Celestine Mwangi"
                  required
                />

                <FormField
                  label="Email"
                  value={formData.email}
                  onChange={(v) => setFormData({...formData, email: v})}
                  onBlur={() => {
                    if (formData.email.trim() && !formData.email.includes('@')) {
                      setErrors({...errors, email: "Enter a valid email address"});
                    } else {
                      const { email, ...rest } = errors;
                      setErrors(rest);
                    }
                  }}
                  error={errors.email}
                  type="email"
                  placeholder="john@example.com"
                  required
                />

                <FormField
                  label="Phone"
                  value={formData.phoneNumber}
                  onChange={(v) => setFormData({...formData, phoneNumber: v})}
                  onBlur={() => {
                    const phoneRegex = /^(0|254|\+254)[17]\d{8}$/;
                    if (formData.phoneNumber.trim() && !phoneRegex.test(formData.phoneNumber.replace(/[\s\-]/g, ''))) {
                      setErrors({...errors, phoneNumber: "Enter a valid Kenyan phone number (07XX or 01XX)"});
                    } else {
                      const { phoneNumber, ...rest } = errors;
                      setErrors(rest);
                    }
                  }}
                  error={errors.phoneNumber}
                  type="tel"
                  placeholder="0712345678"
                  required
                />
              </div>

              {/* Investment Summary */}
              {pricePreview && (
                <div className="bg-gray-900 text-white rounded-lg p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Your Program Investment</p>
                      <p className="text-xs text-gray-500">
                        {pricePreview.weeksCount} week{pricePreview.weeksCount !== 1 ? 's' : ''} • {pricePreview.trainingMode === "VIRTUAL" ? "Online" : "Hybrid"}
                      </p>
                    </div>
                    {pricePreview.discount > 0 && (
                      <span className="text-xs bg-emerald-500 text-white px-3 py-1 rounded-full font-bold flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        SAVE {pricePreview.discount.toLocaleString()}
                      </span>
                    )}
                  </div>
                  {pricePreview.discount > 0 && (
                    <div className="text-lg text-gray-400 line-through mb-1">
                      KSh {pricePreview.totalAmount.toLocaleString()}
                    </div>
                  )}
                  <div className="text-3xl font-bold mb-2">
                    KSh {pricePreview.finalAmount.toLocaleString()}
                  </div>
                  <p className="text-xs text-gray-400">
                    Flexible payment options available in next step
                  </p>
                </div>
              )}

              {/* Submit Button */}
              <button
                onClick={handleEnrollmentSubmit}
                disabled={loading || selectedWeeks.size === 0 || !formData.name.trim() || !formData.email.trim() || !formData.phoneNumber.trim()}
                className="w-full bg-maroon text-white py-4 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-maroon/90 transition flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    Continue to Secure Your Spot
                    <TrendingUp className="w-5 h-5" />
                  </>
                )}
              </button>

              <p className="text-xs text-center text-gray-500">
                Secure enrollment • Flexible payment plans available
              </p>

              {errors.submit && (
                <p className="text-sm text-red-600 text-center">{errors.submit}</p>
              )}
            </div>
          )}

          {/* PAYMENT STEP */}
          {step === "payment-phone" && pricePreview && (
            <div className="space-y-6">
              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                <p className="font-semibold text-gray-900 mb-2">🎓 Your Program Includes:</p>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>✓ {pricePreview.weeksCount} weeks of expert-led training</li>
                  <li>✓ Professional certification upon completion</li>
                  <li>✓ Lifetime access to course materials</li>
                  <li>✓ Job placement assistance</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Enrollment #{enrollmentId}</p>
                <p className="font-medium text-gray-900">{formData.name}</p>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                  <span className="text-sm text-gray-700">Program investment</span>
                  <span className="text-lg font-bold text-gray-900">KSh {pricePreview.finalAmount.toLocaleString()}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-900">Choose Payment Method</label>
                <div className="space-y-2">
                  {[
                    { value: "stk" as const, icon: Smartphone, label: "M-Pesa STK Push", desc: "Instant & secure" },
                    { value: "manual" as const, icon: CreditCard, label: "Paybill Payment", desc: "Pay at your convenience" }
                  ].map(({ value, icon: Icon, label, desc }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setPaymentMethod(value)}
                      className={`w-full text-left border rounded-lg p-4 transition-all flex items-center gap-3 ${
                        paymentMethod === value ? "border-maroon border-l-4 bg-red-50" : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <Icon className="w-5 h-5 text-maroon" />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{label}</div>
                        <div className="text-xs text-gray-600">{desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {paymentMethod === "stk" && (
                <FormField
                  label="M-Pesa Phone Number"
                  value={mpesaPhone}
                  onChange={setMpesaPhone}
                  error={errors.mpesaPhone}
                  type="tel"
                  placeholder="0712345678"
                  required
                />
              )}

              {paymentMethod === "manual" && <PaybillDetails onCopy={copyToClipboard} />}

              <div className="flex gap-3">
                <button
                  onClick={() => setStep("form")}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition"
                >
                  Back
                </button>
                <button
                  onClick={handlePaymentSubmit}
                  disabled={loading}
                  className="flex-1 bg-maroon text-white py-3 rounded-lg font-semibold disabled:opacity-50 hover:bg-maroon/90 transition"
                >
                  {loading ? "Processing..." : paymentMethod === "stk" ? "Complete Enrollment" : "Continue"}
                </button>
              </div>
            </div>
          )}

          {/* PROCESSING */}
          {step === "processing" && (
            <div className="py-12 text-center">
              <Loader2 className="w-12 h-12 animate-spin text-maroon mx-auto mb-4" />
              <p className="text-gray-600">Processing your enrollment...</p>
            </div>
          )}

          {/* POLLING */}
          {step === "polling" && (
            <div className="py-8 text-center space-y-6">
              <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
                <Clock className="w-10 h-10 text-amber-600 animate-pulse" />
              </div>
              <div>
                <p className="font-semibold mb-2 text-gray-900">Confirming Payment</p>
                <p className="text-sm text-gray-600">Check your phone for M-Pesa prompt</p>
                <p className="text-lg font-mono font-bold text-maroon mt-2">{pollingElapsed}s</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-900">
                  Enter PIN on <strong>{mpesaPhone}</strong> to complete
                </p>
              </div>
            </div>
          )}

          {/* SUCCESS */}
          {step === "success" && (
            <div className="py-8 space-y-6">
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <div className="text-center">
                <p className="font-bold text-xl text-gray-900 mb-2">
                  {paymentMethod === "manual" ? "Spot Reserved!" : "Welcome to the Program! 🎉"}
                </p>
                <p className="text-sm text-gray-600">
                  {paymentMethod === "manual" 
                    ? "Complete payment to activate your access" 
                    : "Your journey to becoming a digital marketer starts now"}
                </p>
              </div>

              {paymentMethod === "manual" && <PaybillDetails onCopy={copyToClipboard} />}

              {transactionRef && paymentMethod === "stk" && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-xs text-gray-600">Transaction ID</p>
                  <p className="font-mono font-bold text-gray-900">{transactionRef}</p>
                  {mpesaReceiptNumber && (
                    <>
                      <p className="text-xs text-gray-600 mt-2">Receipt</p>
                      <p className="font-mono font-bold text-gray-900">{mpesaReceiptNumber}</p>
                    </>
                  )}
                </div>
              )}

              <div className="bg-turquoise-50 border border-turquoise-200 rounded-lg p-4">
                <p className="text-sm font-medium mb-2 text-gray-900">📧 Next Steps</p>
                <ul className="text-xs space-y-1 text-gray-800">
                  <li>✓ Confirmation sent to {formData.email}</li>
                  <li>✓ WhatsApp group invitation coming soon</li>
                  <li>✓ Access course materials before Feb 17</li>
                  {paymentMethod === "manual" && <li>✓ Send SMS to +254 704 544 323 after paying</li>}
                </ul>
              </div>

              <button
                onClick={resetAndClose}
                className="w-full bg-maroon text-white py-3 rounded-lg font-semibold hover:bg-maroon/90 transition"
              >
                Done
              </button>
            </div>
          )}

          {/* FAILED & TIMEOUT */}
          {(step === "failed" || step === "timeout") && (
            <div className="py-8 space-y-6">
              <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
                <AlertCircle className="w-10 h-10 text-amber-600" />
              </div>
              <div className="text-center">
                <p className="font-semibold text-gray-900 mb-1">
                  {step === "failed" ? "Payment Not Completed" : "Taking Longer Than Expected"}
                </p>
                <p className="text-sm text-gray-600">
                  {step === "failed" ? "Let's try another method" : "You can still secure your spot"}
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => setStep("payment-phone")}
                  className="w-full bg-maroon text-white py-3 rounded-lg font-semibold hover:bg-maroon/90 transition flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Try Different Method
                </button>

                <a
                  href="https://wa.me/254704544323?text=I%20need%20help%20completing%20enrollment"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition"
                >
                  <MessageCircle className="w-4 h-4" />
                  Get Help
                </a>
              </div>

              <div className="pt-4 border-t">
                <p className="text-sm font-medium text-center mb-3 text-gray-900">Alternative: Paybill Payment</p>
                <PaybillDetails onCopy={copyToClipboard} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}